import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HeaderVariant from '../common/HeaderVariant';
import { Link } from 'react-router-dom';
import Footer from '../Footer/Footer';

const SuccessAnimation = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <motion.div
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.5 }}
      className="bg-white text-black p-8 rounded-lg shadow-xl text-center"
    >
      <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Success checkmark">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold mb-2">Registration Complete!</h3>
      <p className="text-gray-600">Thank you for registering with Jacks.</p>
    </motion.div>
  </motion.div>
);

const ClientForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    use: 'Personal',
    zipCode: '',
    heardAboutUsSource: '',
    heardAboutUsDetail: '',
    disclaimer: false
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare the data for Google Sheets
      const sheetData = [
        new Date().toLocaleString(), // Column A: Date
        formData.zipCode,            // Column B: ZIP Code
        formData.phone,              // Column C: Phone Number
        formData.email,              // Column D: Email
        formData.use,                // Column E: Use
        `${formData.firstName} ${formData.lastName}`, // Column F: Name
        formData.disclaimer ? "Agree" : "Not Agreed"  // Column G: Terms and Disclaimer
      ];

      // Send data to Google Sheets
      const response = await fetch('https://script.google.com/macros/s/AKfycbzDq_DpBS4BF9ag6kc12SC1F9MP2ZxpKeColrlFHaCQxpb3quI9UYBINs9StvjdlsFh/exec', {
        redirect: 'follow',
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: sheetData
        })
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        use: 'Personal',
        zipCode: '',
        heardAboutUsSource: '',
        heardAboutUsDetail: '',
        disclaimer: false
      });

    } catch (error) {
      console.error('Submission error:', error);
      alert('An error occurred. Please try again.');
    }

    setIsSubmitting(false);
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const inputClassName = "w-full bg-[#1A1A1A] text-white px-4 py-3 rounded-md border border-gray-800 focus:border-white focus:ring-1 focus:ring-white transition-colors";

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter your first name"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) {
                    let formattedPhone = '';
                    if (value.length > 0) formattedPhone = '(' + value.slice(0,3);
                    if (value.length > 3) formattedPhone += ') ' + value.slice(3,6);
                    if (value.length > 6) formattedPhone += '-' + value.slice(6,10);
                    setFormData({ ...formData, phone: formattedPhone });
                  }
                }}
                required
                className={inputClassName}
                placeholder="(000) 000-0000"
                maxLength={14}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter your email"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.firstName || !formData.lastName || !formData.phone || !formData.email}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.firstName || !formData.lastName || !formData.phone || !formData.email) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="use" className="block text-sm font-medium text-gray-300 mb-2">
                Use
              </label>
              <select
                name="use"
                id="use"
                value={formData.use}
                onChange={(e) => setFormData({ ...formData, use: e.target.value })}
                required
                className={inputClassName}
              >
                <option value="Personal">Personal</option>
                <option value="Work">Work</option>
                <option value="Leisure">Leisure</option>
                <option value="Travel">Travel</option>
                <option value="All above">All above</option>
              </select>
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-300 mb-2">
                Zip Code
              </label>
              <input
                type="text"
                name="zipCode"
                id="zipCode"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter your zip code"
              />
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="heardAboutUsSource" className="block text-sm font-medium text-gray-300 mb-2">
                  How did you hear about us?
                </label>
                <select
                  name="heardAboutUsSource"
                  id="heardAboutUsSource"
                  value={formData.heardAboutUsSource}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    heardAboutUsSource: e.target.value,
                    heardAboutUsDetail: ''
                  })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select an option</option>
                  <option value="socialMedia">Social Media</option>
                  <option value="friend">Friend</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.heardAboutUsSource && (
                <div>
                  {formData.heardAboutUsSource === 'socialMedia' ? (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="socialMediaPlatform" className="block text-sm font-medium text-gray-300 mb-2">
                          Which platform?
                        </label>
                        <select
                          name="socialMediaPlatform"
                          id="socialMediaPlatform"
                          value={formData.socialMediaPlatform}
                          onChange={(e) => setFormData({ ...formData, socialMediaPlatform: e.target.value })}
                          required
                          className={inputClassName}
                        >
                          <option value="">Select platform</option>
                          <option value="Instagram">Instagram</option>
                          <option value="Facebook">Facebook</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="TikTok">TikTok</option>
                        </select>
                      </div>
                      {formData.socialMediaPlatform && (
                        <div>
                          <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                            Enter username
                          </label>
                          <input
                            type="text"
                            name="heardAboutUsDetail"
                            id="heardAboutUsDetail"
                            value={formData.heardAboutUsDetail}
                            onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                            required
                            className={inputClassName}
                            placeholder={`@username on ${formData.socialMediaPlatform}`}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                        {formData.heardAboutUsSource === 'friend' ? "Friend's name" :
                         formData.heardAboutUsSource === 'event' ? 'Event name' :
                         'Please specify'}
                      </label>
                      <input
                        type="text"
                        name="heardAboutUsDetail"
                        id="heardAboutUsDetail"
                        value={formData.heardAboutUsDetail}
                        onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                        required
                        className={inputClassName}
                        placeholder={
                          formData.heardAboutUsSource === 'friend' ? "Enter friend's name" :
                          formData.heardAboutUsSource === 'event' ? 'Enter event name' :
                          'Please specify'
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-start mb-6">
                <div className="flex items-center h-5">
                  <input
                    id="disclaimer"
                    name="disclaimer"
                    type="checkbox"
                    required
                    checked={formData.disclaimer}
                    onChange={(e) => setFormData({
                      ...formData,
                      disclaimer: e.target.checked
                    })}
                    className="w-4 h-4 border border-gray-800 rounded bg-[#1A1A1A] checked:bg-white checked:border-white focus:ring-1 focus:ring-white text-black transition-colors cursor-pointer"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="disclaimer" className="font-light text-gray-300">
                    I agree to the{' '}
                    <Link to="/terms-of-use" className="font-medium text-blue-500 hover:underline">
                      Terms and Conditions
                    </Link>{', '}
                    <Link to="/disclaimer" className="font-medium text-blue-500 hover:underline">
                      Disclaimer
                    </Link>{', '}
                    and acknowledge that my information will be used in accordance with the{' '}
                    <Link to="/privacypolicy" className="font-medium text-blue-500 hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.use || !formData.zipCode || !formData.heardAboutUsSource || (!formData.heardAboutUsDetail && formData.heardAboutUsSource !== 'indeed') || !formData.disclaimer}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(isSubmitting || !formData.use || !formData.zipCode || !formData.heardAboutUsSource || (!formData.heardAboutUsDetail && formData.heardAboutUsSource !== 'indeed') || !formData.disclaimer) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <HeaderVariant />
      
      <div className="relative min-h-screen bg-black">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] select-none pointer-events-none overflow-hidden">
          <h1 className="text-[20vw] font-bold text-white whitespace-nowrap">CLIENT</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 container mx-auto px-4 py-24 pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 pt-24">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Client Registration
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Join our network of valued clients
              </p>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
              <div className="flex justify-between mb-8">
                {[1, 2].map((step) => (
                  <div
                    key={step}
                    className={`flex items-center ${step < 2 ? 'flex-1' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                        ${currentStep >= step ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}
                      `}
                    >
                      {step}
                    </div>
                    {step < 2 && (
                      <div
                        className={`flex-1 h-px mx-2 transition-colors
                          ${currentStep > step ? 'bg-white' : 'bg-gray-800'}
                        `}
                      />
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <AnimatePresence mode="wait">
                  {renderStep()}
                </AnimatePresence>
              </form>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />

      <AnimatePresence>
        {showSuccess && <SuccessAnimation />}
      </AnimatePresence>
    </div>
  );
};

export default ClientForm; 

